import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {Fragment, useEffect, useMemo} from 'react';
import {connect} from 'react-redux';
import {toast} from 'react-toastify';
import {Virtuoso} from 'react-virtuoso';
import {byType as filterByType, setTypesFilter} from '../actions/filter.js';
import {useGetMosaicsQuery} from '../api/apiSlice.js';
import {
  getComparingMosaic,
  getFilteredMosaics,
  getSelectedMosaic,
} from '../selectors/index.js';
import FilterInput from './filter-input.jsx';
import ListContainer from './list.jsx';
import LoadingIndicator from './loading-indicator.jsx';
import MosaicListItem from './mosaic-list-item.jsx';
import Panel from './panel.jsx';

const FlexWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  overflow: 'hidden',
});

const CheckboxLabel = styled(FormControlLabel)({
  width: '50%',
  marginRight: 0,
  marginLeft: 0,
});

const StyledCheckbox = styled(Checkbox)(({theme}) => ({
  padding: theme.spacing(0.5),
}));

// NOTE: We're using a manual list here to have mosaic types appear in a better order
const mosaicTypes = ['Quarterly', 'Monthly', 'Weekly', 'Daily', 'Basemap'];

const MosaicList = ({
  filteredMosaics,
  nameFilter,
  typesFilter,
  filterByType,
  setTypesFilter,
  ...props
}) => {
  const {data: mosaics, isLoading, error} = useGetMosaicsQuery();

  useEffect(() => {
    if (mosaics) {
      const types = mosaics?.reduce((types, mosaic) => {
        types[mosaic.type] = true;
        return types;
      }, {});
      setTypesFilter(types);
    }
  }, [mosaics, setTypesFilter]);

  if (error) {
    toast.error('There was an error fetching basemaps');
  }

  const selectedMosaicIndex = useMemo(
    () =>
      filteredMosaics?.findIndex(mosaic => mosaic.id === props.selected?.id),
    [filteredMosaics, props.selected],
  );

  const renderMosaic = index => {
    const mosaic = filteredMosaics[index];
    return (
      <MosaicListItem
        active={mosaic === props.selected}
        comparing={mosaic === props.comparing}
        key={mosaic.id}
        mosaic={mosaic}
      />
    );
  };

  const renderList = () => {
    if (isLoading) {
      return <LoadingIndicator>Loading basemaps...</LoadingIndicator>;
    }

    return (
      <FlexWrapper>
        <Paper
          elevation={2}
          square
          sx={theme => ({
            px: 2,
            py: 1,
            flexShrink: 0,
            ...theme.applyStyles('light', {backgroundColor: 'grey.200'}),
          })}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
            }}
          >
            Basemap type
          </Typography>

          <FormGroup row data-qe="mosaic-filters">
            {mosaicTypes.map(mosaicType => {
              const selected = !!typesFilter[mosaicType];

              return (
                <CheckboxLabel
                  key={mosaicType}
                  control={
                    <StyledCheckbox
                      checked={selected}
                      onChange={() => {
                        filterByType({
                          [mosaicType]: !selected,
                        });
                      }}
                      value={mosaicType}
                      color="primary"
                    />
                  }
                  label={mosaicType === 'Basemap' ? 'Other' : mosaicType} // Rename "basemap" to "other"
                  data-qe={`${mosaicType.toLowerCase()}-checkbox`}
                />
              );
            })}
          </FormGroup>
        </Paper>

        <Divider />

        <Paper
          elevation={1}
          square
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="overline">Basemaps</Typography>

          <Typography variant="caption">
            {filteredMosaics.length} basemaps found
          </Typography>
        </Paper>

        <Divider />

        <ListContainer>
          <Fragment>
            {filteredMosaics.length === 0 ? (
              <Typography
                align="center"
                variant="body1"
                color="textSecondary"
                data-qe="no-results-message"
              >
                No basemaps found
              </Typography>
            ) : (
              <Virtuoso
                data-qe="results-list"
                totalCount={filteredMosaics.length}
                initialTopMostItemIndex={
                  selectedMosaicIndex >= 0 ? selectedMosaicIndex : 0
                }
                itemContent={renderMosaic}
              />
            )}
          </Fragment>
        </ListContainer>
      </FlexWrapper>
    );
  };

  return (
    <Panel open={props.open}>
      <Box
        sx={{
          p: 1,
        }}
      >
        <FilterInput />
      </Box>
      <Divider />
      {renderList()}
    </Panel>
  );
};

MosaicList.propTypes = {
  selected: PropTypes.object,
  comparing: PropTypes.object,
  open: PropTypes.bool,
  nameFilter: PropTypes.string,
  filteredMosaics: PropTypes.array,
  filterByType: PropTypes.func,
  setTypesFilter: PropTypes.func,
  typesFilter: PropTypes.object,
};

const mapStateToProps = state => ({
  selected: getSelectedMosaic(state),
  comparing: getComparingMosaic(state),
  nameFilter: state.filter.name,
  filteredMosaics: getFilteredMosaics(state),
  typesFilter: state.filter.types,
});

const mapDispatchProps = {
  filterByType,
  setTypesFilter,
};

export default connect(mapStateToProps, mapDispatchProps)(MosaicList);
